import { useState } from 'react';
import { Box, Flex, Icon, Text } from '@/ui';
import type { IconName } from '@/ui/Icon/Icon';
import { NavItem } from './NavItem';

// ── Types ──────────────────────────────────────────────────────────────────────

export type FullScreenSection = {
  /** Trailing marker in the rail for a section needing attention. */
  badge?: React.ReactNode;
  /** Render function — defers instantiation until the section is actually shown. */
  content: () => React.ReactNode;
  /** A named app icon, or an arbitrary node (e.g. a brand/source mark) to render verbatim. */
  icon: IconName | React.ReactNode;
  id: string;
  label: string;
};

export type FullScreenHeader = {
  highlight: string;
  icon: IconName;
  subtitlePrefix: string;
  subtitleSuffix: string;
  title: string;
};

export type FullScreenRailAction = {
  icon: IconName;
  label: string;
  /** Rendered in the content area while the action is active; `close` returns to the nav sections. */
  render: (close: () => void) => React.ReactNode;
};

type Props = {
  header: FullScreenHeader;
  railAction?: FullScreenRailAction;
  /** Must contain at least one section. TypeScript enforces this via the non-empty tuple type. */
  sections: [FullScreenSection, ...FullScreenSection[]];
  /**
   * Controlled mode: when provided, the panel derives its active section and
   * rail-action state from these props (e.g. driven by a URL param) and bypasses
   * its internal state. Omit them for uncontrolled (local-state) behavior.
   */
  activeId?: string;
  onSelectSection?: (id: string) => void;
  isActionActive?: boolean;
  onActivateAction?: () => void;
  onCloseAction?: () => void;
};

// ── FullScreenPanel ────────────────────────────────────────────────────────────

export const FullScreenPanel = ({
  header,
  railAction,
  sections,
  activeId: controlledActiveId,
  onSelectSection,
  isActionActive: controlledIsActionActive,
  onActivateAction,
  onCloseAction,
}: Props) => {
  const [localActiveId, setLocalActiveId] = useState(sections[0].id);
  const [localActionActive, setLocalActionActive] = useState(false);

  const activeId = controlledActiveId ?? localActiveId;
  const isActionActive = controlledIsActionActive ?? localActionActive;

  const selectSection = (id: string) => {
    if (onSelectSection) {
      onSelectSection(id);
    } else {
      setLocalActiveId(id);
      setLocalActionActive(false);
    }
  };
  const activateAction = () => {
    if (onActivateAction) onActivateAction();
    else setLocalActionActive(true);
  };
  const closeAction = () => {
    if (onCloseAction) onCloseAction();
    else setLocalActionActive(false);
  };

  const activeSection = sections.find((s) => s.id === activeId) ?? sections[0];

  return (
    // Fills the MotionBox overlay; outer padding creates the card-in-panel look.
    <Flex h="100%" p={6}>
      <Flex
        bg="white"
        borderRadius="ah-2xl"
        boxShadow="0 2px 12px rgba(60,50,110,0.08)"
        flex={1}
        overflow="hidden"
      >
        {/* ── Left rail ──────────────────────────────────────────────────── */}
        <Flex direction="column" flexShrink={0} p={4} w="280px">
          {/* Panel header */}
          <Box mb={5} px={1}>
            <Flex align="center" gap={1.5} mb={2}>
              <Icon color="magicPurple.600" icon={header.icon} size={3.5} />
              <Text
                color="magicPurple.600"
                fontSize="ah-label"
                fontWeight={700}
                letterSpacing="ah-label"
                textTransform="uppercase"
              >
                {header.title}
              </Text>
            </Flex>
            <Text color="ink.soft" fontSize="ah-control" lineHeight={1.4}>
              {header.subtitlePrefix}
              <Text as="span" color="magicPurple.600" fontWeight={700}>
                {header.highlight}
              </Text>
              {header.subtitleSuffix}
            </Text>
          </Box>

          {/* Optional rail action CTA (e.g. "Add source") — rendered above nav items */}
          {railAction && (
            <Flex
              align="center"
              bg={isActionActive ? 'magicPurple.25' : 'white'}
              borderColor={isActionActive ? 'magicPurple.300' : 'line.DEFAULT'}
              borderRadius="ah-lg"
              borderStyle="solid"
              borderWidth="1px"
              cursor="pointer"
              gap={2}
              mb={2.5}
              px={3}
              py={2.5}
              transition="border-color .14s, box-shadow .14s"
              w="100%"
              _hover={
                isActionActive
                  ? undefined
                  : {
                      borderColor: 'rgba(0,0,0,0.2)',
                      boxShadow: '0 1px 3px rgba(0,0,0,.06)',
                    }
              }
              onClick={activateAction}
            >
              <Icon
                color={isActionActive ? 'magicPurple.600' : 'ink.soft'}
                icon={railAction.icon}
                size={3.5}
              />
              <Text
                color={isActionActive ? 'magicPurple.700' : 'ink.soft'}
                fontSize="ah-control"
                fontWeight={600}
              >
                {railAction.label}
              </Text>
            </Flex>
          )}

          {/* Nav items */}
          <Flex direction="column" gap={0.5}>
            {sections.map((section) => (
              <NavItem
                badge={section.badge}
                icon={section.icon}
                isActive={!isActionActive && section.id === activeId}
                key={section.id}
                label={section.label}
                onClick={() => selectSection(section.id)}
              />
            ))}
          </Flex>
        </Flex>

        {/* ── Content area — call content() to defer instantiation ───────── */}
        <Box flex={1} overflowY="auto" p={8}>
          {isActionActive && railAction
            ? railAction.render(closeAction)
            : activeSection.content()}
        </Box>
      </Flex>
    </Flex>
  );
};
